import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Pill } from '@/shared/components';
import { I } from '@/shared/icons';
import { toApiError, useApi, type PropertyResponse } from '@/shared/api';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { propertyKeys } from './queries';
import { ChannexPreflightDialog } from './channex-publish/ChannexPreflightDialog';

export function ObjectPublicationBanner({ property }: { property: PropertyResponse }) {
  const api = useApi();
  const queryClient = useQueryClient();
  // Veroeffentlichen verlangt WorkspaceAdmin (Server: ChannexProvisioningController) und wirkt
  // auf das eigene Inventar — der Tenant kommt aus dem Token.
  const user = useAuth().user;
  const admin = isWorkspaceAdmin(user);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [parentQueued, setParentQueued] = useState(false);
  const group = useQuery({
    queryKey: ['property-groups', property.propertyGroupId],
    queryFn: () => api.propertyGroups.get(property.propertyGroupId as string),
    enabled: !!property.propertyGroupId,
  });

  const resume = useMutation({
    mutationFn: () => api.channex.provisionProperty(property.id),
    onSuccess: async () => {
      setParentQueued(true);
      await queryClient.invalidateQueries({ queryKey: propertyKeys.detail(property.id) });
      toast.success('Objekt-Veröffentlichung wurde erneut eingeplant.');
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  // Freigabe ohne Kanalmanager: ohne diesen Weg bliebe ein Apartment, das bewusst nicht
  // synchronisiert, dauerhaft Entwurf — der einzige andere Schreiber von Active ist der
  // Provisioning-Erfolg. Bewusst die zurückhaltende Aktion neben der Veröffentlichung.
  const releaseWithout = useMutation({
    mutationFn: () => api.properties.releaseWithoutChannelManager(property.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyKeys.detail(property.id) });
      toast.success('Apartment ohne Kanalmanager freigegeben.');
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  if (parentQueued) {
    return (
      <div className="border-amber/40 bg-amber/10 mb-4 flex items-center gap-3 rounded-md border p-3">
        <I name="Loader" size={16} className="text-amber" />
        <span className="text-ink text-sm">
          Objekt-Veröffentlichung ist eingeplant und wird im Hintergrund verarbeitet.
        </span>
      </div>
    );
  }

  const parentBinding = group.data?.parentBinding;
  if (parentBinding) {
    const confirmed =
      parentBinding.status === 'Succeeded' &&
      parentBinding.lastConfirmedVersion >= parentBinding.desiredVersion;
    if (confirmed) {
      return (
        <div className="border-sage/30 bg-sage/10 mb-4 flex items-center gap-2 rounded-md border p-3">
          <I name="Check" size={16} className="text-sage" />
          <span className="text-ink text-sm">Objekt bei Channex bestätigt.</span>
        </div>
      );
    }
    if (parentBinding.status === 'RequiresManualReconcile') {
      return (
        <div className="border-sealwax/40 bg-sealwax/5 mb-4 rounded-md border p-3">
          <span className="text-ink text-sm">Manueller Channex-Abgleich erforderlich.</span>
        </div>
      );
    }
    if (parentBinding.status === 'Failed') {
      return (
        <div className="border-sealwax/40 bg-sealwax/5 mb-4 rounded-md border p-4">
          <div className="flex items-start gap-3">
            <I name="Error" size={18} className="text-sealwax mt-0.5" />
            <div className="flex-1">
              <Pill variant="error" size="sm">
                Objekt-Veröffentlichung fehlgeschlagen
              </Pill>
              {parentBinding.lastError && (
                <p className="text-ink-soft mt-2 text-xs">{parentBinding.lastError}</p>
              )}
              {admin && (
                <Button
                  variant="brand"
                  size="sm"
                  className="mt-3"
                  loading={resume.isPending}
                  disabled={resume.isPending}
                  leftIcon={<I name="Refresh" size={14} />}
                  onClick={() => resume.mutate()}
                >
                  Erneut einplanen
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="border-amber/40 bg-amber/10 mb-4 flex items-center gap-3 rounded-md border p-3">
        <I name="Loader" size={16} className="text-amber" />
        <span className="text-ink text-sm">
          Objekt-Veröffentlichung ist eingeplant und wird im Hintergrund verarbeitet.
        </span>
      </div>
    );
  }

  // Ohne Objekt-Bindung ist nichts veroeffentlicht — Admin-only Publish-Aktion
  // (Staff/Owner sehen keine Aktion).
  if (!admin) return null;
  return (
    <>
      <div className="border-line bg-card-alt/60 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border p-4">
        <div className="flex items-center gap-2">
          <I name="Webhook" size={16} className="text-postmark" />
          <span className="text-ink text-sm">
            Objekt noch nicht bei Channex veröffentlicht — als Entwurf gespeichert.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            loading={releaseWithout.isPending}
            disabled={releaseWithout.isPending}
            onClick={() => releaseWithout.mutate()}
            title="Live schalten, ohne das Apartment bei Channex anzulegen — für Objekte, die bewusst nicht synchronisiert werden."
          >
            Ohne Kanalmanager freigeben
          </Button>
          <Button
            variant="brand"
            size="sm"
            leftIcon={<I name="Bolt" size={14} />}
            onClick={() => setPreflightOpen(true)}
          >
            Objekt prüfen und einplanen
          </Button>
        </div>
      </div>
      <ChannexPreflightDialog
        property={property}
        open={preflightOpen}
        onOpenChange={setPreflightOpen}
        onQueued={() => setParentQueued(true)}
      />
    </>
  );
}
